import { useState } from 'react';
import { contactInfo, faqs as contactFaqs } from '../../../mocks/contacto';
import { useSupabaseAdmin, useSupabaseAdminObject } from '../../../hooks/useSupabaseAdmin';
import WidgetDailyAppointments from './WidgetDailyAppointments';
import { useSupabaseAppointments } from '../../../hooks/useSupabaseAppointments';
import type { Appointment } from '../../../mocks/appointments';

interface ContactInfoData {
  address: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  phone: string;
  email: string;
  mapLink: string;
  altaCosturaSchedule: string;
  arreglosSchedule: string;
  tiendaSchedule: string;
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

function serializeContactInfo(): ContactInfoData {
  return {
    address: contactInfo.address,
    city: contactInfo.city,
    postalCode: contactInfo.postalCode,
    region: contactInfo.region,
    country: contactInfo.country,
    phone: contactInfo.phone,
    email: contactInfo.email,
    mapLink: contactInfo.mapLink,
    altaCosturaSchedule: contactInfo.hours.altaCostura.schedule,
    arreglosSchedule: contactInfo.hours.arreglos.schedule,
    tiendaSchedule: contactInfo.hours.tienda.schedule,
  };
}

export default function AdminContact() {
  const contactData = useSupabaseAdminObject<ContactInfoData>('contact_info', 'key', serializeContactInfo());
  const faqData = useSupabaseAdmin<FAQItem>({
    table: 'faqs',
    initialData: contactFaqs.map((f, i) => ({ ...f, id: i + 1 })),
  });
  const {
    appointments, types, loading: apptLoading,
    updateAppointment,
  } = useSupabaseAppointments();

  const [activeSubTab, setActiveSubTab] = useState<'info' | 'faq' | 'mybookings'>('info');
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQItem | null>(null);
  const [addingFaq, setAddingFaq] = useState(false);
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);
  const [adminNote, setAdminNote] = useState('');

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    contactData.update({
      address: fd.get('address') as string,
      city: fd.get('city') as string,
      postalCode: fd.get('postalCode') as string,
      region: fd.get('region') as string,
      country: fd.get('country') as string,
      phone: fd.get('phone') as string,
      email: fd.get('email') as string,
      mapLink: fd.get('mapLink') as string,
      altaCosturaSchedule: fd.get('altaCosturaSchedule') as string,
      arreglosSchedule: fd.get('arreglosSchedule') as string,
      tiendaSchedule: fd.get('tiendaSchedule') as string,
    });
    setShowContactForm(false);
  };

  const handleFaqSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      question: fd.get('question') as string,
      answer: fd.get('answer') as string,
    };
    if (editingFaq) {
      faqData.update(editingFaq.id, payload);
      setEditingFaq(null);
    } else {
      faqData.add(payload);
      setAddingFaq(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[
          { key: 'info' as const, label: 'Datos de Contacto' },
          { key: 'faq' as const, label: `FAQ Contacto (${faqData.data.length})` },
          { key: 'mybookings' as const, label: 'Mis Reservas' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSubTab(tab.key)}
            className={`px-4 py-2 rounded-full font-sans text-sm whitespace-nowrap transition-colors ${
              activeSubTab === tab.key
                ? 'bg-brand-black text-white'
                : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black hover:border-brand-gray-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'mybookings' && (
        <WidgetDailyAppointments
          appointments={appointments}
          types={types}
          loading={apptLoading}
          onEdit={(appt) => { setEditingAppt(appt); setAdminNote(appt.admin_notes || ''); }}
        />
      )}

      {activeSubTab === 'info' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-xl">Datos de Contacto</h3>
            <button
              onClick={() => setShowContactForm(true)}
              className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-pencil-line mr-1" />
              Editar datos
            </button>
          </div>

          {showContactForm && (
            <form
              onSubmit={handleContactSubmit}
              className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h4 className="font-serif text-brand-black text-lg">Editar Datos de Contacto</h4>
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Dirección</label>
                  <input name="address" defaultValue={contactData.data.address} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Ciudad</label>
                  <input name="city" defaultValue={contactData.data.city} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Código Postal</label>
                  <input name="postalCode" defaultValue={contactData.data.postalCode} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Región</label>
                  <input name="region" defaultValue={contactData.data.region} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">País</label>
                  <input name="country" defaultValue={contactData.data.country} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Teléfono</label>
                  <input name="phone" defaultValue={contactData.data.phone} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Email</label>
                <input name="email" type="email" defaultValue={contactData.data.email} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Enlace Google Maps</label>
                <input name="mapLink" defaultValue={contactData.data.mapLink} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Horario Alta Costura</label>
                  <input name="altaCosturaSchedule" defaultValue={contactData.data.altaCosturaSchedule} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Horario Arreglos</label>
                  <input name="arreglosSchedule" defaultValue={contactData.data.arreglosSchedule} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Horario Tienda</label>
                  <input name="tiendaSchedule" defaultValue={contactData.data.tiendaSchedule} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button type="submit" className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
                  Guardar cambios
                </button>
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-map-pin-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Dirección</h4>
              <p className="font-sans text-brand-gray-600 text-sm mt-1">{contactData.data.address}</p>
              <p className="font-sans text-brand-gray-500 text-xs mt-0.5">
                {contactData.data.city}, {contactData.data.postalCode}, {contactData.data.country}
              </p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-phone-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Teléfono</h4>
              <p className="font-sans text-brand-gray-600 text-sm mt-1">{contactData.data.phone}</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-mail-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Email</h4>
              <p className="font-sans text-brand-gray-600 text-sm mt-1">{contactData.data.email}</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-time-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Horario Alta Costura</h4>
              <p className="font-sans text-brand-gray-600 text-sm mt-1">{contactData.data.altaCosturaSchedule}</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-time-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Horario Arreglos</h4>
              <p className="font-sans text-brand-gray-600 text-sm mt-1">{contactData.data.arreglosSchedule}</p>
            </div>
            <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black mb-3">
                <i className="ri-global-line text-lg" />
              </div>
              <h4 className="font-sans text-brand-black text-sm font-medium">Google Maps</h4>
              <a href={contactData.data.mapLink} target="_blank" rel="noopener noreferrer" className="font-sans text-brand-gray-600 text-sm mt-1 hover:text-brand-black transition-colors break-all">
                {contactData.data.mapLink}
              </a>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'faq' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-xl">FAQ Contacto General</h3>
            <button
              onClick={() => { setAddingFaq(true); setEditingFaq(null); }}
              className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              + Nueva pregunta
            </button>
          </div>

          {(addingFaq || editingFaq) && (
            <form
              onSubmit={handleFaqSubmit}
              className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h4 className="font-serif text-brand-black text-lg">
                  {editingFaq ? 'Editar Pregunta' : 'Nueva Pregunta'}
                </h4>
                <button
                  type="button"
                  onClick={() => { setEditingFaq(null); setAddingFaq(false); }}
                  className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Pregunta *</label>
                <input
                  name="question"
                  defaultValue={editingFaq?.question}
                  required
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
                />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Respuesta *</label>
                <textarea
                  name="answer"
                  defaultValue={editingFaq?.answer}
                  rows={4}
                  required
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button type="submit" className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
                  Guardar
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingFaq(null); setAddingFaq(false); }}
                  className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div className="space-y-3">
            {faqData.data.map((faq) => (
              <div key={faq.id} className="bg-white border border-brand-gray-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-brand-black text-sm font-medium">
                      {faq.question}
                    </h4>
                    <p className="font-sans text-brand-gray-500 text-sm mt-2 leading-relaxed line-clamp-3">
                      {faq.answer}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditingFaq(faq); setAddingFaq(false); }}
                      className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                    >
                      <i className="ri-pencil-line text-xs" />
                    </button>
                    <button
                      onClick={() => { if (confirm('¿Eliminar esta pregunta?')) faqData.remove(faq.id); }}
                      className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* APPOINTMENT EDIT MODAL from Widget */}
      {editingAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-brand-black text-xl">Editar Cita</h3>
              <button
                onClick={() => setEditingAppt(null)}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                    Fecha
                  </label>
                  <p className="font-sans text-brand-black text-sm mt-1">
                    {editingAppt.preferred_date}
                  </p>
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                    Hora
                  </label>
                  <p className="font-sans text-brand-black text-sm mt-1">
                    {editingAppt.preferred_time}
                  </p>
                </div>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Cliente
                </label>
                <p className="font-sans text-brand-black text-sm mt-1">
                  {editingAppt.name} · {editingAppt.email}
                </p>
                {editingAppt.phone && (
                  <p className="font-sans text-brand-gray-500 text-sm">
                    {editingAppt.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Servicio
                </label>
                <p className="font-sans text-brand-black text-sm mt-1">
                  {editingAppt.service_type}
                </p>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Estado del pago
                </label>
                <div className="mt-2 flex items-center gap-3">
                  {editingAppt.paid ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium bg-emerald-50 text-emerald-700">
                      <i className="ri-checkbox-circle-line text-base" />
                      Pagada online
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-sans text-sm font-medium bg-amber-50 text-amber-700">
                      <i className="ri-time-line text-base" />
                      Pendiente de pago
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Notas del cliente
                </label>
                <p className="font-sans text-brand-gray-500 text-sm mt-1">
                  {editingAppt.notes || '—'}
                </p>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                  Notas internas (admin)
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
                  placeholder="Notas solo visibles para el equipo..."
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={async () => {
                    await updateAppointment(editingAppt.id, { admin_notes: adminNote });
                    setEditingAppt(null);
                    setAdminNote('');
                  }}
                  className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
                >
                  Guardar notas
                </button>
                <button
                  onClick={() => { setEditingAppt(null); setAdminNote(''); }}
                  className="border border-brand-gray-200 text-brand-black font-sans text-sm px-5 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}